import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NavBar,
  Button,
  TextArea,
  Selector,
  Toast,
  ImageViewer,
  Space,
} from 'antd-mobile';
import { CameraOutline, DeleteOutline, EyeOutline } from 'antd-mobile-icons';
import { zavhozApi } from '../api';
import { ZAVHOZ_CATEGORIES } from './zavhozMeta';

const MAX_PHOTOS = 5;

function ZavhozNewPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [description, setDescription] = useState('');
  const [category, setCategory] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const photosRef = useRef([]);
  photosRef.current = photos;

  useEffect(() => {
    return () => {
      photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, []);

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      Toast.show({ icon: 'fail', content: `Максимум ${MAX_PHOTOS} фото` });
      return;
    }
    if (files.length > room) {
      Toast.show({ icon: 'fail', content: `Можно добавить не более ${MAX_PHOTOS} фото` });
    }
    const previews = files.slice(0, room).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...previews]);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].url);
      next.splice(idx, 1);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      Toast.show({ icon: 'fail', content: 'Опишите проблему' });
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('description', description.trim());
      if (category.length) {
        formData.append('category', category[0]);
      }
      photos.forEach((p) => formData.append('photos', p.file));
      const res = await zavhozApi.create(formData);
      Toast.show({ icon: 'success', content: 'Заявка отправлена' });
      const newId = res?.data?.id;
      if (newId) {
        navigate(`/zavhoz/${newId}`, { replace: true });
      } else {
        navigate('/zavhoz', { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      Toast.show({ icon: 'fail', content: typeof detail === 'string' ? detail : 'Ошибка отправки заявки' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '0 12px 100px' }}>
      <NavBar
        onBack={() => navigate(-1)}
        style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
      >
        Новая заявка
      </NavBar>

      <div style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
          Описание проблемы <span style={{ color: '#ff4d4f' }}>*</span>
        </div>
        <TextArea
          placeholder="Например: в каб. 215 течёт кран"
          value={description}
          onChange={setDescription}
          rows={4}
          maxLength={1000}
          showCount
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: 12,
            border: '1px solid #f0f0f0',
          }}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
          Категория (необязательно)
        </div>
        <Selector
          options={ZAVHOZ_CATEGORIES.map((c) => ({ label: c, value: c }))}
          value={category}
          onChange={setCategory}
          style={{ '--border-radius': '10px' }}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
          Фото (до {MAX_PHOTOS})
        </div>
        <input
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFilesChange}
        />
        <Button
          block
          color="primary"
          fill="outline"
          onClick={() => fileInputRef.current?.click()}
          style={{ borderRadius: 12 }}
          disabled={photos.length >= MAX_PHOTOS}
        >
          <CameraOutline style={{ marginRight: 6 }} />
          Сделать фото
        </Button>
      </div>

      {photos.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>
            Выбрано фото: {photos.length} (нажмите для просмотра)
          </div>
          <Space wrap>
            {photos.map((p, idx) => (
              <div key={idx} style={{ position: 'relative', width: 88, height: 88 }}>
                <img
                  src={p.url}
                  alt="preview"
                  onClick={() => {
                    setPreviewIndex(idx);
                    setPreviewVisible(true);
                  }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                />
                <div
                  onClick={() => removePhoto(idx)}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#ff4d4f',
                    color: '#fff',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  <DeleteOutline style={{ fontSize: 12 }} />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 24,
                    background: 'rgba(0,0,0,0.4)',
                    borderRadius: '0 0 10px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EyeOutline style={{ color: '#fff', fontSize: 14 }} />
                </div>
              </div>
            ))}
          </Space>
        </div>
      )}

      <div style={{ marginTop: 28 }}>
        <Button
          block
          color="primary"
          loading={submitting}
          onClick={handleSubmit}
          style={{ borderRadius: 12, height: 46 }}
        >
          Отправить заявку
        </Button>
      </div>

      <ImageViewer.Multi
        images={photos.map((p) => p.url)}
        visible={previewVisible}
        defaultIndex={previewIndex}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  );
}

export default ZavhozNewPage;
